// @ts-nocheck
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { PageHeader, StatusBadge, Card, CardHeader, CardTitle, CardContent, Button, ConfirmationModal, showToast } from '@/components/ui';
import { cn } from '@/lib/utils';
import { sellers, sellerDetailData } from '@/data/sellersMockData';
import { OrderInsightDrawer } from '@/components/orders/OrderInsightDrawer';
import {
  Package, ShoppingCart, CalendarDays, DollarSign, AlertTriangle,
  Percent, Clock,
  Ban, Pause, RotateCcw, CreditCard, UserCog, Trash2,
  Download, FileSpreadsheet, FileText,
  CheckCircle2, XCircle, Info,
  ShoppingBag, RefreshCw, Store,
  ChevronDown, Eye, RotateCw,
} from 'lucide-react';

const planColors = {
  Free: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
  Silver: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
  Gold: 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400',
  Platinum: 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400',
};
const statusMap = { Active: 'ACTIVE', Paused: 'PAUSED', Blocked: 'BLOCKED', Warning: 'WARNING' };

const tabDefs = [
  { key: 'overview', label: 'Overview' },
  { key: 'orders', label: 'Orders' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'sync', label: 'Sync Logs' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'reports', label: 'Reports' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'activity', label: 'Activity Log' },
];

const kpiDefs = [
  { key: 'totalProducts', label: 'Total Products', icon: Package, color: 'indigo' },
  { key: 'activeOrders', label: 'Active Orders', icon: ShoppingCart, color: 'emerald' },
  { key: 'ordersToday', label: 'Orders Today', icon: CalendarDays, color: 'blue' },
  { key: 'revenue30d', label: 'Revenue (30d)', icon: DollarSign, color: 'teal', fmt: 'currency' },
  { key: 'inventoryAlerts', label: 'Inventory Alerts', icon: AlertTriangle, color: 'amber' },
  { key: 'syncFailures', label: 'Sync Failures', icon: RefreshCw, color: 'rose' },
  { key: 'refundRate', label: 'Refund Rate %', icon: Percent, color: 'orange' },
  { key: 'avgFulfillment', label: 'Avg Fulfillment', icon: Clock, color: 'slate' },
];

const iconBg = {
  indigo: 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400',
  emerald: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400',
  blue: 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400',
  teal: 'bg-teal-50 dark:bg-teal-950 text-teal-600 dark:text-teal-400',
  amber: 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400',
  rose: 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400',
  orange: 'bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400',
  slate: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
};

/* ────── helper components ────── */
const BarChart = ({ data, valueKey, labelKey, maxVal }) => {
  const max = maxVal || Math.max(...data.map(d => d[valueKey]));
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-slate-500 dark:text-slate-400 w-10 text-right">{d[labelKey]}</span>
          <div className="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-indigo-500 rounded transition-all" style={{ width: `${(d[valueKey] / max) * 100}%` }} />
          </div>
          <span className="text-xs font-medium text-slate-700 dark:text-slate-300 w-16 text-right">
            ${(d[valueKey] / 1000).toFixed(1)}K
          </span>
        </div>
      ))}
    </div>
  );
};

const OrderStatusBadge = ({ status }) => {
  const m = { Shipped: 'ACTIVE', Processing: 'PENDING', Delivered: 'OK', Returned: 'FAILED' };
  return <StatusBadge status={m[status] || 'DISABLED'} showDot>{status}</StatusBadge>;
};

const StockBadge = ({ status }) => {
  const m = { OK: ['ACTIVE', 'In Stock'], LOW: ['WARNING', 'Low'], CRITICAL: ['FAILED', 'Critical'], OUT: ['BLOCKED', 'Out'] };
  const [s, l] = m[status] || ['DISABLED', status];
  return <StatusBadge status={s} showDot>{l}</StatusBadge>;
};

const SyncStatusBadge = ({ status }) => {
  const m = { COMPLETED: 'ACTIVE', FAILED: 'FAILED', RUNNING: 'PENDING' };
  return <StatusBadge status={m[status] || 'DISABLED'} showDot>{status}</StatusBadge>;
};

const NotifIcon = ({ type }) => {
  const icons = { warning: AlertTriangle, error: XCircle, info: Info, success: CheckCircle2 };
  const colors = { warning: 'text-amber-500', error: 'text-rose-500', info: 'text-blue-500', success: 'text-emerald-500' };
  const I = icons[type] || Info;
  return <I className={cn('w-4 h-4', colors[type])} />;
};

const ActivityIcon = ({ icon }) => {
  const map = {
    order: { I: ShoppingBag, c: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400' },
    sync: { I: RefreshCw, c: 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' },
    inventory: { I: Package, c: 'bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400' },
    error: { I: XCircle, c: 'bg-rose-100 dark:bg-rose-900 text-rose-600 dark:text-rose-400' },
    plan: { I: CreditCard, c: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400' },
    marketplace: { I: Store, c: 'bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-400' },
  };
  const { I, c } = map[icon] || map.sync;
  return <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', c)}><I className="w-4 h-4" /></div>;
};

const fmtDate = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtDateShort = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/* ══════════════════════════════════════ */
export default function SellerDetail() {
  const { id } = useParams();
  const seller = sellers.find(s => s.id === id) || sellers[0];
  const d = sellerDetailData;

  const [tab, setTab] = useState('overview');
  const [modal, setModal] = useState(null);
  const [modalReason, setModalReason] = useState('');
  const [orderFilter, setOrderFilter] = useState('All');
  const [insightOrderId, setInsightOrderId] = useState(null);

  const handleAction = (action) => setModal(action);
  const confirmAction = () => {
    showToast.success(`${modal} confirmed`, `Action "${modal}" applied to ${seller.name}.`);
    setModal(null);
    setModalReason('');
  };

  const fmtKPI = (key, val) => {
    if (key === 'revenue30d') return `$${val.toLocaleString()}`;
    if (key === 'refundRate') return `${val}%`;
    return String(val);
  };

  const filteredOrders = orderFilter === 'All' ? d.orders : d.orders.filter(o => o.status === orderFilter);

  return (
    <DashboardLayout activePath="/sellers">
      <PageHeader
        title={`Seller: ${seller.name}`}
        subtitle={`${seller.workspaceId}  ·  ${seller.plan}  ·  Joined ${fmtDateShort(seller.joinedDate)}`}
        breadcrumbs={[
          { label: 'Sellers', active: false, href: '/sellers' },
          { label: seller.name, active: true },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button data-testid="action-block" variant="outline" size="sm" onClick={() => handleAction('Block Seller')}>
              <Ban className="w-4 h-4" /> Block
            </Button>
            <Button data-testid="action-pause" variant="outline" size="sm" onClick={() => handleAction('Pause Seller')}>
              <Pause className="w-4 h-4" /> Pause
            </Button>
            <Button data-testid="action-reset-sync" variant="outline" size="sm" onClick={() => handleAction('Reset Sync')}>
              <RotateCcw className="w-4 h-4" /> Reset Sync
            </Button>
            <Button data-testid="action-change-plan" variant="outline" size="sm" onClick={() => handleAction('Change Plan')}>
              <CreditCard className="w-4 h-4" /> Plan
            </Button>
            <Button data-testid="action-impersonate" variant="secondary" size="sm" onClick={() => showToast.info('Impersonate', `Viewing as ${seller.name}...`)}>
              <UserCog className="w-4 h-4" /> Impersonate
            </Button>
            <Button data-testid="action-delete" variant="outline" size="sm" className="text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950" onClick={() => handleAction('Delete Seller')}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        }
      />

      {/* Seller sub-header badges */}
      <div className="flex items-center gap-3 mb-6">
        <span className={cn('px-2.5 py-1 text-xs font-semibold rounded-full', planColors[seller.plan])}>{seller.plan}</span>
        <StatusBadge status={statusMap[seller.status]} showDot />
        {seller.marketplaces.map(m => (
          <span key={m} className="px-2 py-0.5 text-[10px] font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">{m}</span>
        ))}
      </div>

      {/* KPI Cards */}
      <div data-testid="seller-kpi-grid" className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 mb-8">
        {kpiDefs.map((kpi) => {
          const Icon = kpi.icon;
          const val = d.kpis[kpi.key];
          const isNeg = val.delta.startsWith('-');
          return (
            <Card key={kpi.key} data-testid={`seller-kpi-${kpi.key}`}>
              <CardContent className="p-3">
                <div className={cn('p-1.5 rounded-md w-fit mb-2', iconBg[kpi.color])}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                </div>
                <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{fmtKPI(kpi.key, val.value)}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">{kpi.label}</span>
                  <span className={cn('text-[10px] font-medium', isNeg ? 'text-rose-500' : 'text-emerald-500')}>
                    {val.delta}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto">
        <nav className="flex gap-6 min-w-max">
          {tabDefs.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={cn(
                'pb-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                tab === t.key
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ════ Tab Content ════ */}
      <div data-testid="tab-content">

        {/* ─── Overview ─── */}
        {tab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Revenue Trend (6 Months)</CardTitle></CardHeader>
              <CardContent><BarChart data={d.revenueMonthly} valueKey="revenue" labelKey="month" /></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Marketplace Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {d.marketplaceRevenue.map((m) => (
                    <div key={m.marketplace} className="flex items-center gap-3">
                      <span className="text-sm text-slate-700 dark:text-slate-300 w-20">{m.marketplace}</span>
                      <div className="flex-1 h-4 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${m.pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 w-12 text-right">{m.pct}%</span>
                      <span className="text-xs text-slate-700 dark:text-slate-300 w-16 text-right">${(m.revenue / 1000).toFixed(1)}K</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-base">Recent Activity</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {d.activityLog.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex items-center gap-3">
                      <ActivityIcon icon={a.icon} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{a.action}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{a.detail}</p>
                      </div>
                      <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{fmtDate(a.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Orders ─── */}
        {tab === 'orders' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Orders</CardTitle>
                <div className="relative">
                  <select data-testid="order-status-filter" value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)} className="appearance-none pl-3 pr-8 py-1.5 text-xs rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none">
                    {['All', 'Processing', 'Shipped', 'Delivered', 'Returned'].map(s => <option key={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table data-testid="orders-table" className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 dark:border-slate-700">
                  {['Order ID', 'Product', 'Marketplace', 'Qty', 'Amount', 'Status', 'Date', ''].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredOrders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-2.5 font-mono text-xs text-indigo-600 dark:text-indigo-400">{o.id}</td>
                      <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{o.product}</td>
                      <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">{o.marketplace}</td>
                      <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{o.qty}</td>
                      <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-slate-100">${o.amount.toLocaleString()}</td>
                      <td className="px-4 py-2.5"><OrderStatusBadge status={o.status} /></td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">{fmtDateShort(o.date)}</td>
                      <td className="px-4 py-2.5"><Button variant="ghost" size="sm" data-testid={`view-order-${o.id}`} onClick={() => setInsightOrderId(o.id)}><Eye className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* ─── Inventory ─── */}
        {tab === 'inventory' && (
          <Card>
            <CardHeader><CardTitle className="text-base">Inventory</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table data-testid="inventory-table" className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 dark:border-slate-700">
                  {['SKU', 'Product', 'Stock', 'Threshold', 'Status', 'Last Sync'].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {d.inventory.map((item) => (
                    <tr key={item.sku} data-testid={`inv-row-${item.sku}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-2.5 font-mono text-xs text-indigo-600 dark:text-indigo-400">{item.sku}</td>
                      <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{item.name}</td>
                      <td className="px-4 py-2.5">
                        <span className={cn('font-medium', item.stock === 0 ? 'text-rose-600 dark:text-rose-400' : item.stock <= item.lowThreshold ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-slate-100')}>
                          {item.stock}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">{item.lowThreshold}</td>
                      <td className="px-4 py-2.5"><StockBadge status={item.status} /></td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">{fmtDate(item.lastSync)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* ─── Catalog ─── */}
        {tab === 'catalog' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Catalog Summary</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{d.catalogSummary.totalSKUs.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total SKUs</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{d.catalogSummary.needsMapping}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Needs Mapping</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{d.catalogSummary.recentlyUpdated}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Recently Updated</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{d.catalogSummary.productTypes.length}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Product Types</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Product Type Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {d.catalogSummary.productTypes.map((pt) => {
                    const pct = Math.round((pt.count / d.catalogSummary.totalSKUs) * 100);
                    return (
                      <div key={pt.type} className="flex items-center gap-3">
                        <span className="text-sm text-slate-700 dark:text-slate-300 w-32 truncate">{pt.type}</span>
                        <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 dark:text-slate-400 w-16 text-right">{pt.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Sync Logs ─── */}
        {tab === 'sync' && (
          <Card>
            <CardHeader><CardTitle className="text-base">Marketplace Sync History</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table data-testid="sync-table" className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 dark:border-slate-700">
                  {['Marketplace', 'Status', 'Started', 'Duration', 'Products', 'Errors', ''].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {d.syncLogs.map((log) => (
                    <tr key={log.id} data-testid={`sync-row-${log.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-slate-100">{log.marketplace}</td>
                      <td className="px-4 py-2.5"><SyncStatusBadge status={log.status} /></td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">{fmtDate(log.startedAt)}</td>
                      <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{log.duration}</td>
                      <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{log.products.toLocaleString()}</td>
                      <td className="px-4 py-2.5">
                        {log.errors > 0 ? <span className="text-rose-600 dark:text-rose-400 font-medium">{log.errors}</span> : <span className="text-slate-400">0</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        {log.status === 'FAILED' && (
                          <Button variant="outline" size="sm" data-testid={`retry-sync-${log.id}`} onClick={() => showToast.info('Retry', `Retrying ${log.marketplace} sync...`)}>
                            <RotateCw className="w-3.5 h-3.5" /> Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* ─── Revenue ─── */}
        {tab === 'revenue' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Monthly Revenue</CardTitle></CardHeader>
              <CardContent><BarChart data={d.revenueMonthly} valueKey="revenue" labelKey="month" /></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Marketplace Revenue Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {d.marketplaceRevenue.map((m) => (
                    <div key={m.marketplace} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{m.marketplace}</span>
                      <span className="text-lg font-bold text-slate-900 dark:text-slate-100">${(m.revenue / 1000).toFixed(1)}K</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Net vs Gross & Refund Deductions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {d.revenueMonthly.map((m) => (
                    <div key={m.month} className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 dark:text-slate-400 w-10 text-right">{m.month}</span>
                      <div className="flex-1 flex gap-1">
                        <div className="h-4 bg-indigo-500 rounded-l" style={{ width: `${((m.revenue - m.refunds) / m.revenue) * 100}%` }} />
                        <div className="h-4 bg-rose-400 rounded-r" style={{ width: `${(m.refunds / m.revenue) * 100}%` }} />
                      </div>
                      <span className="text-xs text-slate-700 dark:text-slate-300 w-16 text-right">${(m.revenue / 1000).toFixed(1)}K</span>
                      <span className="text-[10px] text-rose-500 w-12 text-right">-${m.refunds}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-3 rounded bg-indigo-500" />Net Revenue</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-3 rounded bg-rose-400" />Refunds</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Reports ─── */}
        {tab === 'reports' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Sales Summary', desc: 'Monthly sales and revenue report', icon: FileText, testid: 'report-sales' },
              { title: 'Export CSV', desc: 'Full catalog and order export', icon: FileSpreadsheet, testid: 'report-csv' },
              { title: 'Download Report', desc: 'Comprehensive seller performance PDF', icon: Download, testid: 'report-download' },
            ].map((r) => (
              <Card key={r.testid}>
                <CardContent className="flex flex-col items-center text-center py-8">
                  <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 mb-4">
                    <r.icon className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">{r.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{r.desc}</p>
                  <Button data-testid={r.testid} variant="outline" size="sm" onClick={() => showToast.info('Download', `${r.title} download started (mock).`)}>
                    <Download className="w-4 h-4" /> Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ─── Notifications ─── */}
        {tab === 'notifications' && (
          <Card>
            <CardHeader><CardTitle className="text-base">Notifications</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {d.notifications.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border',
                      n.read
                        ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
                        : 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800'
                    )}
                  >
                    <NotifIcon type={n.type} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{n.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{n.message}</p>
                    </div>
                    <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{fmtDate(n.date)}</span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0 mt-1" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Activity Log ─── */}
        {tab === 'activity' && (
          <Card>
            <CardHeader><CardTitle className="text-base">Activity Log</CardTitle></CardHeader>
            <CardContent>
              <div className="relative pl-6">
                {/* Timeline line */}
                <div className="absolute left-[15px] top-0 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />
                <div className="space-y-6">
                  {d.activityLog.map((a) => (
                    <div key={a.id} data-testid={`activity-${a.id}`} className="relative flex items-start gap-4">
                      <div className="absolute -left-6 top-0">
                        <ActivityIcon icon={a.icon} />
                      </div>
                      <div className="ml-6 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{a.action}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{a.detail}</p>
                      </div>
                      <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{fmtDate(a.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Order Insight Drawer */}
      <OrderInsightDrawer
        open={!!insightOrderId}
        onClose={() => setInsightOrderId(null)}
        orderId={insightOrderId}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={!!modal}
        onOpenChange={(open) => { if (!open) setModal(null); }}
        title={modal || ''}
        description={`Are you sure you want to ${modal?.toLowerCase()} for ${seller.name}? This action will be logged.`}
        confirmLabel={modal || 'Confirm'}
        variant={modal === 'Delete Seller' || modal === 'Block Seller' ? 'danger' : 'primary'}
        requireReason
        reason={modalReason}
        onReasonChange={setModalReason}
        onConfirm={confirmAction}
        onCancel={() => setModal(null)}
      />
    </DashboardLayout>
  );
}

